//TODO while this is cleaner with a bunch of small arrow functions, it's odd to use since you never know if you should call platform().stuff() or platform.stuff(). Either move this generic platform() sniffer to another sub-function, or make this a class
/**
 * Returns the current platform, as one of the function's constants.
 * @returns {string}
 */
const platform = () => {
	if (window.IS_APP) { // noinspection JSUnresolvedVariable
		return window.cordova.platformId //either "ios" or "android" //eslint-disable-line no-undef
	} else {
		if (navigator.userAgent.indexOf('iPhone') > 0 || navigator.userAgent.indexOf('iPad') > 0) {
			return platform.WEB_IOS
		} else if (navigator.userAgent.indexOf('Android') > 0) {
			return platform.WEB_ANDROID
		} else {
			return platform.WEB
		}
	}
}
platform.IOS          = 'ios'
platform.ANDROID      = 'android'
platform.WEB_IOS      = 'web-ios'
platform.WEB_ANDROID  = 'web-android'
platform.WEB          = 'web'

/**
 * Checks if the current environment seems to be communicating with React development servers. Scenarios:
 * 1. React development server: runs from localhost (or its IPs)
 * 2. dev apps: runs from the filesystem, and the ROOT_URL changes depending on their env.local.json (which is read upon building the app, it's not the same as our env.local here)
 * @return {boolean}
 */
platform.isDev = () => {
	if (platform.isNative()) {
		return false //native is always staging, never development (so far, there's no way to shove the live development React server onto Cordova's webview, with all its plugins); this early return avoids regex matching and avoid a false-positive against cordova-android's new https://localhost webview
	}
	const devRegex = /(127\.0\.0\.1|0\.0\.0\.0|localhost)/
	return !!(window.location.hostname.match(devRegex) || window.ROOT_URL.match(devRegex))
}

/**
 * Checks if the application is running from a staging server.
 * @returns {boolean}
 */
platform.isStg = () => (window.location.hostname.includes('ehungry.net') || window.ROOT_URL.includes('ehungry.net')) && !platform.isDev()

platform.hostname = () => !window.location.hostname.includes('localhost')?
	window.location.hostname :
	(!window.ROOT_URL.includes('localhost')? window.ROOT_URL.substring(8) : false) //no protocol

/**
 * Returns the identified environment.
 * @returns {'development'|'staging'|'production'}
 */
platform.env = () => {
	if (platform.isDev()) {
		return 'development'
	}
	if (platform.isStg()) {
		return 'staging'
	}
	return 'production'
}

/**
 * Are we in production? Or better... are we NOT in an identifiable dev/staging env?
 * @returns {boolean}
 */
platform.isProd = () => platform.env() == 'production'

/**
 * Says if the user is running the Android app or Android webapp.
 * If you want to check for the iOS app only, use `platform() == platform.ANDROID` (or WEB_ANDROID).
 * @return {boolean}
 */
platform.isAndroid = () => [platform.ANDROID, platform.WEB_ANDROID].indexOf(platform()) >= 0

/**
 * Says if the user is running the iOS app or iOS webapp.
 * If you want to check for the iOS app only, use `platform() == platform.IOS` (or WEB_IOS).
 * @return {boolean}
 */
platform.isIOS = () => [platform.IOS, platform.WEB_IOS].indexOf(platform()) >= 0

/**
 * Says if the user is running a browser, mobile or desktop
 * @return {boolean}
 */
platform.isWeb = () => [platform.WEB, platform.WEB_ANDROID, platform.WEB_IOS].indexOf(platform()) >= 0

/**
 * Says if the user is running from a mobile device, either native or web
 * @return {boolean}
 */
platform.isMobile = () => [platform.ANDROID, platform.WEB_ANDROID, platform.IOS, platform.WEB_IOS].indexOf(platform()) >= 0

/**
 * Says if the user is running from a non-mobile device (precisely, something that don't identify as Android or iOS).
 * @return {boolean}
 */
platform.isComputer = () => !platform.isMobile()

/**
 * Says if the user is running the native app, not the web version
 * @return {boolean}
 */
platform.isNative = () => !platform.isWeb()

platform.nativeVersionAtLeast = version => {
	if (!window.ENV?.VERSION) {
		return null //TODO should this always return true instead?
	}
	return semVerStrToInt(window.ENV.VERSION) >= semVerStrToInt(version)
}

/**
 * Sniffs device information to identify if this is an iPad disguising as Mac because Apple is boring
 * This was copied into the Cordova apps' index files!!!
 * @see https://stackoverflow.com/a/59187672/102960
 * @return {boolean}
 */
platform.isReallyAnIPad = () => navigator.userAgent.toLowerCase().includes('macintosh') && navigator?.maxTouchPoints > 2

platform.isIOSSimulator = () => platform.isNative() && platform.isIOS() && window.device?.isVirtual

/**
 * Returns either "tap" or "click" depending on which platform the user is on.
 * @param {function|TFunction} t from i18next
 * @returns {string}
 */
platform.clickOrTap = t => platform.isMobile()? t('tap') : t('click')

/**
 * Returns either "page" or "app" depending on which platform the user is on.
 * @param {function|TFunction} t from i18next
 * @returns {string}
 */
platform.pageOrApp = t => platform.isNative()? t('app') : t('page')

//doesn't have a watcher or the likes because the user is hardly changing screen sizes for the current use-cases
platform.isXSmall = () => window.matchMedia('(max-width: 378px)').matches //taken from _variables.scss

/**
 * Checks if running on in app and a plugin is installed (and optionally plugin version)
 * @param plugin {string} Plugin to check for existence
 * @param versionCallback {function(version: string): boolean} Checks a plugin version.
 * @returns {boolean}
 */
platform.hasPlugin = (plugin, versionCallback = (version => true)) => {
	if (platform.isWeb()) {
		return false
	} else {
		const plugins = window.cordova.require('cordova/plugin_list').metadata //eslint-disable-line no-undef
		if (plugins.hasOwnProperty(plugin)) {
			return versionCallback(plugins[plugin])
		}
		return false
	}
}

/**
 * Calculates a comparable version number based on a semantic version string.
 * Allows up to 99 for minor and patch values.
 * Examples:
 * - 2.16.13 = 20000+1600+13 = 21613
 * - 2.4.1   = 20000+400+1   = 20401
 * - 2.3.15  = 20000+300+15  = 20315
 * @param version {string}
 * @returns {number}
 */
function semVerStrToInt(version) {
	const [major, minor, patch] = version.split('.')
	return parseInt(patch) + (minor * 100) + (major * 100_00)
}

export default platform
